import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { map } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import * as _ from 'lodash';
import { CdkDrag, CdkDragDrop, CdkDropList, transferArrayItem } from '@angular/cdk/drag-drop';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { TemplatesQueries } from 'src/app/shared/types/get-query';
import { MutationQuery } from 'src/app/shared/types/mutation-query.enum';
import { SnackbarType } from 'src/app/shared/components/snackbar/snackbar.component';
import { ConfirmModalComponent } from 'src/app/shared/components/confirm-modal/confirm-modal.component';
import { FilterListComponent } from 'src/app/shared/components/filter-list/filter-list.component';
import { Selectable } from 'src/app/shared/types/selectable.type';
import { Brand } from 'src/app/components/company/models/new/brand.model';
import { Category } from 'src/app/components/company/models/new/category';
import { Collection } from 'src/app/components/company/models/new/collection';
import { Color, Gender, Style } from '../../../models/new/style';
import { StyleGroup } from '../../../models/new/style-group';
import { AspectRatio, MediaFormat, Shot, ShotType } from '../../../models/new/shot';
import { ExportTemplate } from '../../../models/new/export-template';
import { ExportTemplateMapper } from '../../../services/export-template-mapper';
import { ExportChannel } from '../../../models/new/export-channel';
import { ExportChannelMapper } from '../../../services/export-channel-mapper';
import { TemplateMapper } from '../../../services/template-mapper';
import { ColorMapper } from '../../../services/color-mapper';
import { BrandFilterService } from 'src/app/services/filters/brand-filter.service';
import { CategoryFilterService } from 'src/app/services/filters/category-filter.service';
import { CollectionFilterService } from 'src/app/services/filters/collection-filter.service';
import { GenderFilterService } from 'src/app/services/filters/gender-filter.service';
import { StyleGroupFilterService } from 'src/app/services/filters/style-group-filter.service';
import { StyleFilterService } from 'src/app/services/filters/style-filter.service';
import { ColorFilterService } from 'src/app/services/filters/color-filter.service';

const CODIFIED_USE_CELL_COUNT = 256;
const VIDEO_FORMAT_CODES = ['MP4', 'MKV'];

export class CodifiedUseCell {
  code?: string;
  items?: Shot[];
  disabled?: boolean;

  constructor(init?: Partial<CodifiedUseCell>) {
    Object.assign(this, init);
  }
}

@Component({
  selector: 'bt-company-export-template-detail',
  standalone: false,
  templateUrl: './company-export-template-detail.component.html',
  styleUrl: './company-export-template-detail.component.scss'
})
export class CompanyExportTemplateDetailComponent implements OnInit {
  loading: boolean = false;
  exportTemplate: ExportTemplate | null = null;
  exportChannel: ExportChannel | undefined;
  colors: Color[] = [];
  isNew: boolean = false;
  codifiedUseCells: CodifiedUseCell[] = [];
  allShotTypes: ShotType[] = [];
  allAspectRatios: AspectRatio[] = [];
  allMediaFormats: MediaFormat[] = [];

  // Builder state
  pickedShotType: ShotType | null = null;
  pickedAspectRatio: AspectRatio | null = null;
  pickedMediaFormat: MediaFormat | null = null;
  builderChip: Shot[] = [];
  private allowedCodifiedUseCodes = new Set<number>();
  private restrictGridToExportCodes = false;

  // CDK: builder drop zone accepts only empty slot; cells accept only if empty and enabled
  canEnterCell = (drag: CdkDrag<Shot>, drop: CdkDropList<Shot[]>): boolean => {
    if (drop.data.length !== 0) {
      return false;
    }

    const cell = this.codifiedUseCells.find((currentCell) => currentCell.items === drop.data);
    return !cell?.disabled;
  };

  constructor(
    private graphQLService: GraphQLService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private exportTemplateMapper: ExportTemplateMapper,
    private exportChannelMapper: ExportChannelMapper,
    private templateMapper: TemplateMapper,
    private utilitiesService: UtilitiesService,
    private dialog: MatDialog,
    private brandFilterService: BrandFilterService,
    private categoryFilterService: CategoryFilterService,
    private collectionFilterService: CollectionFilterService,
    private genderFilterService: GenderFilterService,
    private styleGroupFilterService: StyleGroupFilterService,
    private styleFilterService: StyleFilterService,
    private colorFilterService: ColorFilterService,
    private colorMapper: ColorMapper
  ) {}

  ngOnInit(): void {
    this.loadShotTypes();
    this.loadAspectRatios();
    this.loadMediaFormats();

    this.activatedRoute.params.subscribe((params) => {
      this.loadExportChannel(Number(params['channelId']), params['exportTemplateId']);
    });
  }

  private loadExportChannel(channelId?: number, exportTemplateId?: string): void {
    if (!channelId) {
      this.router.navigate(['/company/third-parties-applications']);
      return;
    }

    this.graphQLService
      .getData<any>(TemplatesQueries.GET_EXPORT_CHANNELS, {
        where: this.exportChannelMapper.toWhereClauseById(channelId)
      })
      .pipe(
        map((res: any) =>
          (res.data.exportChannels?.nodes || []).map((node: any) => this.exportChannelMapper.toExportChannel(node))
        )
      )
      .subscribe({
        next: (channels: ExportChannel[]) => {
          this.exportChannel = channels[0];

          if (!this.exportChannel) {
            this.router.navigate(['/company/third-parties-applications']);
            return;
          }

          this.loadAllowedExportCodes(() => {
            if (exportTemplateId != 'new') {
              this.getExportTemplate(Number(exportTemplateId));
            } else {
              this.isNew = true;
              this.exportTemplate = new ExportTemplate({ exportChannel: this.exportChannel });
              this.exportTemplate.styles = [];
              this.exportTemplate.shots = [];
              this.buildCodifiedUseGrid();
            }
          });
        },
        error: () => {
          this.router.navigate(['/company/third-parties-applications']);
        }
      });
  }

  onBack(): void {
    this.router.navigate(['/company/third-parties-applications', this.exportChannel?.id]);
  }

  // ─── Loaders ───────────────────────────────────────────────────────────────

  private loadShotTypes(): void {
    this.graphQLService.getData<ShotType>(TemplatesQueries.GET_SHOT_TYPES).subscribe({
      next: (res: any) => {
        this.allShotTypes = res.data.shotTypes.nodes.map((st: any) =>
          this.exportTemplateMapper.toExportTemplateShotType(st)
        );
      },
      error: () => {
        this.utilitiesService.showSnackbar('Error loading shot types', SnackbarType.Error);
      }
    });
  }

  private loadAspectRatios(): void {
    this.graphQLService.getData<AspectRatio>(TemplatesQueries.GET_ASPECT_RATIOS).subscribe({
      next: (res: any) => {
        this.allAspectRatios = res.data.aspectRatios.nodes.map((ar: any) => this.templateMapper.toAspectRatio(ar));
      },
      error: () => {
        this.utilitiesService.showSnackbar('Error loading aspect ratios', SnackbarType.Error);
      }
    });
  }

  private loadMediaFormats(): void {
    this.graphQLService.getData<MediaFormat>(TemplatesQueries.GET_MEDIA_FORMATS).subscribe({
      next: (res: any) => {
        this.allMediaFormats = res.data.mediaFormats.nodes.map((mf: any) => this.templateMapper.toMediaFormat(mf));
      },
      error: () => {
        this.utilitiesService.showSnackbar('Error loading media formats', SnackbarType.Error);
      }
    });
  }

  // ─── Grid ──────────────────────────────────────────────────────────────────

  private loadAllowedExportCodes(done: () => void): void {
    this.allowedCodifiedUseCodes.clear();
    this.restrictGridToExportCodes = !!this.exportChannel?.usesCodifiedUses;

    if (!this.restrictGridToExportCodes) {
      done();
      return;
    }

    this.graphQLService
      .getData<any>(TemplatesQueries.GET_EXPORT_CODES)
      .pipe(map((res: any) => this.exportTemplateMapper.toCodifiedUseCodes(res.data.exportCodes?.nodes || [])))
      .subscribe({
        next: (codes: string[]) => {
          codes.forEach((code) => {
            const numericCode = Number(code);
            if (!isNaN(numericCode)) {
              this.allowedCodifiedUseCodes.add(numericCode);
            }
          });
          done();
        },
        error: () => {
          this.utilitiesService.showSnackbar('Error loading export codes', SnackbarType.Error);
          done();
        }
      });
  }

  private isGridCellDisabled(code: number): boolean {
    if (!this.restrictGridToExportCodes) {
      return false;
    }

    return !this.allowedCodifiedUseCodes.has(code);
  }

  private buildCodifiedUseGrid(): void {
    const shots = this.exportTemplate?.shots || [];

    this.codifiedUseCells = Array.from({ length: CODIFIED_USE_CELL_COUNT }, (_unused, index) => {
      const code = index;
      const shot = shots.find((s) => Number(s.code) === code);

      return new CodifiedUseCell({
        code: code.toString(),
        items: shot ? [shot] : [],
        disabled: this.isGridCellDisabled(code)
      });
    });
  }

  // ─── Builder ───────────────────────────────────────────────────────────────

  selectShotType(st: ShotType): void {
    if (this.pickedShotType?.id === st.id) {
      this.pickedShotType = null;
    } else {
      this.pickedShotType = st;
    }
    this.rebuildBuilderChip();
  }

  selectAspectRatio(ar: AspectRatio): void {
    if (this.isAspectRatioDisabled(ar)) {
      return;
    }

    if (this.pickedAspectRatio?.id === ar.id) {
      this.pickedAspectRatio = null;
    } else {
      this.pickedAspectRatio = ar;
    }
    this.rebuildBuilderChip();
  }

  selectMediaFormat(mf: MediaFormat): void {
    if (this.isMediaFormatDisabled(mf)) {
      return;
    }

    if (this.pickedMediaFormat?.id === mf.id) {
      this.pickedMediaFormat = null;
    } else {
      this.pickedMediaFormat = mf;
    }
    this.rebuildBuilderChip();
  }

  isAspectRatioDisabled(ar: AspectRatio): boolean {
    if (!this.exportChannel?.usesCodifiedUses) {
      return false;
    }

    return (ar.label || '').toUpperCase() !== '1-1';
  }

  isMediaFormatDisabled(mf: MediaFormat): boolean {
    if (!this.exportChannel?.usesCodifiedUses) {
      return false;
    }

    return (mf.code || '').toUpperCase() !== 'JPG';
  }

  private rebuildBuilderChip(): void {
    this.builderChip.splice(0);
    if (this.pickedShotType && this.pickedAspectRatio && this.pickedMediaFormat) {
      this.builderChip.push(
        new Shot({
          shotType: this.pickedShotType,
          aspectRatio: this.pickedAspectRatio,
          mediaFormat: this.pickedMediaFormat
        })
      );
    }
  }

  /** True if the current builder combination is already placed in the grid. */
  isCombinationPlaced(): boolean {
    if (!this.pickedShotType || !this.pickedAspectRatio || !this.pickedMediaFormat) return false;

    for (const cell of this.codifiedUseCells) {
      const shot = cell.items?.[0];
      if (
        shot?.shotType?.id === this.pickedShotType.id &&
        shot?.aspectRatio?.id === this.pickedAspectRatio.id &&
        shot?.mediaFormat?.id === this.pickedMediaFormat.id
      ) {
        return true;
      }
    }
    return false;
  }

  isVideoFormat(shot: Shot): boolean {
    return VIDEO_FORMAT_CODES.includes((shot.mediaFormat?.code || '').toUpperCase());
  }

  get builderChipShot(): Shot | null {
    return this.builderChip[0] || null;
  }

  // ─── Drag & Drop ───────────────────────────────────────────────────────────

  drop(event: CdkDragDrop<Shot[]>): void {
    if (event.previousContainer === event.container) return;

    const isBuilderTarget = event.container.data === this.builderChip;

    if (isBuilderTarget) {
      // Chip dragged from a grid cell back to the builder drop zone
      const droppedShot = event.previousContainer.data[event.previousIndex];
      this.builderChip.splice(0);
      event.previousContainer.data.splice(event.previousIndex, 1);
      this.builderChip.push(droppedShot);
      this.pickedShotType = droppedShot?.shotType || null;
      this.pickedAspectRatio = droppedShot?.aspectRatio || null;
      this.pickedMediaFormat = droppedShot?.mediaFormat || null;
      return;
    }

    // Grid cell target: only accept if cell is empty and enabled
    const targetCell = this.codifiedUseCells.find((cell) => cell.items === event.container.data);
    if (targetCell?.disabled || event.container.data.length > 0) return;

    const isBuilderSource = event.previousContainer.data === this.builderChip;
    transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);

    if (isBuilderSource) {
      // Chip left the builder — reset picker selections
      this.pickedShotType = null;
      this.pickedAspectRatio = null;
      this.pickedMediaFormat = null;
      // builderChip is now empty (transferArrayItem moved the item out)
    }
  }

  // ─── Filters / Scope ───────────────────────────────────────────────────────

  canSelectCollectionAndCategory(): boolean {
    return !!this.exportTemplate?.brand;
  }

  hasCollectionOrCategorySelection(): boolean {
    return !!this.exportTemplate?.category || !!this.exportTemplate?.collection;
  }

  canSelectGenderStyleGroupAndStyle(): boolean {
    return this.canSelectCollectionAndCategory();
  }

  canSelectColors(): boolean {
    return !!this.exportTemplate?.styles?.length && this.exportTemplate.styles.length === 1;
  }

  private resetStylesAndColors(): void {
    if (!this.exportTemplate) return;

    this.exportTemplate.styles = [];
    this.exportTemplate.colors = [];
    this.colors = [];
  }

  private resetDeepScopeFilters(): void {
    if (!this.exportTemplate) return;

    this.exportTemplate.gender = undefined;
    this.exportTemplate.styleGroup = undefined;
    this.resetStylesAndColors();
  }

  private enforceScopeHierarchy(): void {
    if (!this.exportTemplate) return;

    if (!this.exportTemplate.brand) {
      this.exportTemplate.collection = undefined;
      this.exportTemplate.category = undefined;
      this.resetDeepScopeFilters();
    }
  }

  getExportTemplate(exportTemplateId: number) {
    this.loading = true;

    this.graphQLService
      .getData<ExportTemplate>(TemplatesQueries.GET_EXPORT_TEMPLATES, {
        where: this.exportTemplateMapper.toWhereClauseById(exportTemplateId),
        first: 1
      })
      .pipe(map((res: any) => this.exportTemplateMapper.toExportTemplate(res.data.exportCodifiedUseMaps.nodes[0])))
      .subscribe({
        next: (data: ExportTemplate) => {
          this.exportTemplate = data;
          this.colors = data.colors ? data.colors.map((c) => this.colorMapper.toColor(c)) : [];
          this.buildCodifiedUseGrid();
          this.loading = false;
        },
        error: (err: any) => {
          console.error('Error loading export template', err);
          this.utilitiesService.showSnackbar('Error loading export template', SnackbarType.Error);
          this.loading = false;
        }
      });
  }

  saveExportTemplate() {
    if (!this.exportTemplate) return;

    this.dialog
      .open(ConfirmModalComponent, {
        data: {
          title: 'Save',
          message: 'Are you sure you want to save the changes to this export template?'
        }
      })
      .afterClosed()
      .subscribe((confirm: boolean) => {
        if (!confirm || !this.exportTemplate) return;

        this.loading = true;

        const shots: Shot[] = [];
        this.codifiedUseCells.forEach((cell) => {
          if (cell.disabled) return;

          const shot = cell.items?.[0];
          if (!shot?.shotType?.id) return;

          shots.push(
            new Shot({
              shotType: shot.shotType,
              aspectRatio: shot.aspectRatio,
              mediaFormat: shot.mediaFormat,
              code: cell.code
            })
          );
        });

        const exportTemplateToSave = new ExportTemplate({
          ...this.exportTemplate,
          exportChannel: this.exportChannel,
          shots
        });

        const isUpdate = !!exportTemplateToSave.id;
        const mutation = isUpdate ? MutationQuery.UPDATE_EXPORT_TEMPLATE : MutationQuery.CREATE_EXPORT_TEMPLATE;
        const input = isUpdate
          ? this.exportTemplateMapper.toUpdateExportTemplateQL(exportTemplateToSave)
          : this.exportTemplateMapper.toCreateExportTemplateQL(exportTemplateToSave);

        this.graphQLService.postData(mutation, { input }).subscribe({
          next: (res: any) => {
            const errorMessage = this.getGraphQlErrorMessage(res);
            if (errorMessage) {
              this.utilitiesService.showSnackbar(errorMessage, SnackbarType.Error);
              return;
            }
            this.loading = false;

            this.utilitiesService.showSnackbar('Export template saved successfully', SnackbarType.Success);
          },
          error: (err: any) => {
            this.loading = false;
            this.utilitiesService.showSnackbar(
              this.getGraphQlErrorMessage(err) || 'An error occurred while saving the export template',
              SnackbarType.Error
            );
          }
        });
      });
  }

  private getGraphQlErrorMessage(err: any): string | undefined {
    const graphQlErrors = [
      ...(err?.networkError?.error?.errors || []),
      ...(err?.networkError?.result?.errors || []),
      ...(err?.networkError?.errors || [])
    ];

    for (const graphQlError of graphQlErrors) {
      const extensions = graphQlError?.extensions;
      const message =
        (typeof extensions?.get === 'function' ? extensions.get('Message') || extensions.get('message') : undefined) ||
        extensions?.Message ||
        extensions?.message ||
        graphQlError?.message;

      if (typeof message === 'string' && message.trim()) {
        return message.trim();
      }
    }

    return undefined;
  }

  codeToNumber(code: string): number {
    return code ? parseInt(code, 10) : 0;
  }

  // ─── Filter modals ─────────────────────────────────────────────────────────

  openBrandFilterModal(): void {
    if (this.exportTemplate && !this.exportTemplate.isDefault) {
      this.dialog
        .open(FilterListComponent<Brand>, {
          data: {
            title: 'Brand',
            singleSelection: true,
            selected: this.exportTemplate.brand
              ? [
                  {
                    ...this.exportTemplate?.brand,
                    displayId: this.exportTemplate.brand.id,
                    displayName: this.exportTemplate.brand.name,
                    selected: true
                  }
                ]
              : [],
            filterService: this.brandFilterService
          },
          width: '400px'
        })
        .afterClosed()
        .subscribe({
          next: (res: Array<Selectable<Brand>>) => {
            if (res) {
              if (this.exportTemplate) {
                this.exportTemplate.brand = res.length ? res[0] : undefined;
                this.exportTemplate.collection = undefined;
                this.exportTemplate.category = undefined;
                this.resetDeepScopeFilters();
                this.enforceScopeHierarchy();
              }
            }
          }
        });
    }
  }

  openCollectionFilterModal(): void {
    if (this.exportTemplate) {
      this.dialog
        .open(FilterListComponent<Collection>, {
          data: {
            title: 'Collection',
            singleSelection: true,
            selected: this.exportTemplate.collection
              ? [
                  {
                    ...this.exportTemplate.collection,
                    displayId: this.exportTemplate.collection.id,
                    displayName: this.exportTemplate.collection.name,
                    selected: true
                  }
                ]
              : [],
            filterService: this.collectionFilterService
          },
          width: '400px'
        })
        .afterClosed()
        .subscribe({
          next: (res: Array<Selectable<Collection>>) => {
            if (res && this.exportTemplate) {
              this.exportTemplate.collection = res.length
                ? {
                    id: res[0].displayId,
                    name: res[0].displayName,
                    startDate: res[0].startDate,
                    endDate: res[0].endDate
                  }
                : undefined;
              this.resetStylesAndColors();
              this.enforceScopeHierarchy();
            }
          }
        });
    }
  }

  openCategoryFilterModal(): void {
    if (this.exportTemplate) {
      this.dialog
        .open(FilterListComponent<Category>, {
          data: {
            title: 'Category',
            singleSelection: true,
            selected: this.exportTemplate.category
              ? [
                  {
                    ...this.exportTemplate?.category,
                    displayId: this.exportTemplate.category.id,
                    displayName: this.exportTemplate.category.name,
                    selected: true
                  }
                ]
              : [],
            filterService: this.categoryFilterService
          },
          width: '400px'
        })
        .afterClosed()
        .subscribe({
          next: (res: Array<Selectable<Category>>) => {
            if (res) {
              if (this.exportTemplate) {
                this.exportTemplate.category = res.length ? res[0] : undefined;
                this.resetStylesAndColors();
                this.enforceScopeHierarchy();
              }
            }
          }
        });
    }
  }

  openGenderFilterModal(): void {
    if (this.exportTemplate) {
      this.dialog
        .open(FilterListComponent<Gender>, {
          data: {
            title: 'Gender',
            singleSelection: true,
            selected: this.exportTemplate.gender
              ? [
                  {
                    ...this.exportTemplate.gender,
                    displayId: this.exportTemplate.gender.id,
                    displayName: this.exportTemplate.gender.name,
                    selected: true
                  }
                ]
              : [],
            filterService: this.genderFilterService
          },
          width: '400px'
        })
        .afterClosed()
        .subscribe({
          next: (res: Array<Selectable<Gender>>) => {
            if (res && this.exportTemplate) {
              this.exportTemplate.gender = res.length ? { id: res[0].displayId, name: res[0].displayName } : undefined;
              this.resetStylesAndColors();
            }
          }
        });
    }
  }

  openStyleGroupFilterModal(): void {
    if (this.exportTemplate) {
      this.dialog
        .open(FilterListComponent<StyleGroup>, {
          data: {
            title: 'Style Group',
            singleSelection: true,
            selected: this.exportTemplate.styleGroup
              ? [
                  {
                    ...this.exportTemplate.styleGroup,
                    displayId: this.exportTemplate.styleGroup.id,
                    displayName: this.exportTemplate.styleGroup.name,
                    selected: true
                  }
                ]
              : [],
            filterService: this.styleGroupFilterService
          },
          width: '400px'
        })
        .afterClosed()
        .subscribe({
          next: (res: Array<Selectable<StyleGroup>>) => {
            if (res && this.exportTemplate) {
              this.exportTemplate.styleGroup = res.length
                ? { id: res[0].displayId, name: res[0].displayName }
                : undefined;
              this.resetStylesAndColors();
            }
          }
        });
    }
  }

  openStylesFilterModal(): void {
    if (this.exportTemplate) {
      this.dialog
        .open(FilterListComponent<Style>, {
          data: {
            title: 'Styles',

            selected: this.exportTemplate.styles
              ? this.exportTemplate.styles.map((style) => ({
                  ...style,
                  displayId: style.modelId,
                  displayName: style.code + ' - ' + style.name,
                  selected: true
                }))
              : [],
            variables: {
              brandIds: this.exportTemplate.brand?.id ? [this.exportTemplate.brand.id] : undefined,
              categoryIds: this.exportTemplate.category?.id ? [this.exportTemplate.category.id] : undefined,
              collectionIds: this.exportTemplate.collection?.id ? [this.exportTemplate.collection.id] : undefined,
              genderIds: this.exportTemplate.gender?.id ? [this.exportTemplate.gender.id] : undefined,
              styleGroupIds: this.exportTemplate.styleGroup?.id ? [this.exportTemplate.styleGroup.id] : undefined
            },
            filterService: this.styleFilterService
          },
          width: '400px'
        })
        .afterClosed()
        .subscribe({
          next: (res: Selectable<Style>[]) => {
            if (res) {
              if (this.exportTemplate)
                this.exportTemplate.styles = res.length ? res.map((style) => ({ ...style })) : [];

              this.colors = [];
            }
          }
        });
    }
  }

  openColorsFilterModal(): void {
    if (!this.exportTemplate || !this.exportTemplate.styles || !this.exportTemplate.styles.length) return;

    let style = this.exportTemplate.styles[0];

    this.dialog
      .open(FilterListComponent<Color>, {
        data: {
          title: 'Colors',
          selected:
            style.colors && style.colors.length
              ? style.colors.map((color) => ({
                  ...color,
                  displayId: color.id,
                  displayName: `${color.code} - ${color.name}`,
                  selected: true
                }))
              : [],
          variables: { modelId: style.modelId },
          filterService: this.colorFilterService
        }
      })
      .afterClosed()
      .subscribe({
        next: (res: Selectable<Color>[]) => {
          if (res) {
            this.colors = res.map((color) => ({ ...color }));
            style.colors = _.cloneDeep(res);
          }
        }
      });
  }
}
